import { IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { BorderLeft, ContentCopy, Delete, Edit, Loop, Send } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Announcements, AnnouncementsPriority, AnnouncementStatus, Priority, UserType } from "../../Types/UnitFeatureTypes";
// import { announcementsInfo } from "../../Data/DummyData";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Actions } from "../../Redux/Reducer/rootReducer";
import WarningDialog from "../CommonComponents/WarningDialog";
import { useAuth } from "../../context/authContext";
// import { IS_MANAGER } from "../../Utils/Constants";

interface BuildTableDataProps {
    item: Announcements[];
}

const BuildAnnouncementTable: React.FC<BuildTableDataProps> = ({
    item,
}) => {

    const navigate = useNavigate();
    const {user} = useAuth();

    const IS_MANAGER = user?.type === UserType.MANAGER;
    const [deleteAnnouncementId, setDeleteAnnouncementId] = useState<number>(-1);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const dispatch = useDispatch();

    const handleAnnouncementExpand = (id: number) => {
        navigate(`/viewAnnouncement`, { state: { id: id } });
    }

    const handleDeleteAnnouncement = () => {
        if (deleteAnnouncementId !== -1) {
            dispatch(Actions.announcement.deleteAnnouncementRequestAction.request({id: deleteAnnouncementId.toString()}));
            setShowWarningDialog(false);
        }
    }

    const handleSendAnnouncement = (id: number) => {
        navigate('/sendAnnouncement', { state: { announcementId: id } });
    }

    const handleEditAnnouncement = (announcement: Announcements) => {
        navigate('/editAnnouncement', { state: { edit: true, announcement: announcement } });
    }

    const getPriorityColor = (priority: AnnouncementsPriority) => {
        switch (priority) {
            case AnnouncementsPriority.HIGH:
                return '#fc585d';
            case AnnouncementsPriority.MEDIUM:
                return '#FCB15A';
            case AnnouncementsPriority.LOW:
                return 'cyan';
            case AnnouncementsPriority.INFORMATIONAL:
                return 'green';
            default:
                return 'white';
        }
    }

    const getTableData = (item: Announcements[]) => {
        return (<Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{fontWeight: 600}} align="center" >Date</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="center">Title</TableCell>
                    {IS_MANAGER && <>
                        <TableCell sx={{fontWeight: 600}} align="center">Seen By</TableCell>
                        <TableCell sx={{fontWeight: 600}} align="center">Status</TableCell>
                        <TableCell sx={{fontWeight: 600}} align="center">Quick Actions</TableCell>
                    </>}
                </TableRow>
            </TableHead>
            <TableBody>
                {item.map((announcement: Announcements) =>
                    <TableRow key={announcement.id} sx={
                        {   borderLeftColor: getPriorityColor(announcement.priority),
                            borderLeftWidth: 5,
                            borderLeftStyle: 'solid',
                            '&:hover': {
                            backgroundColor: '#f5f5f5',
                            cursor: 'pointer'
                    }}} onClick={(e) => {
                        if ((e.target as HTMLElement).closest('#actions')) return;
                        handleAnnouncementExpand(announcement.id);
                      }}>
                        <TableCell align="center">{new Date(announcement.created_on).toDateString()}</TableCell>
                        <TableCell sx={{maxWidth: 100}} align="center">
                            <Tooltip title={announcement.title}>
                                <Typography variant="body2" color="textSecondary" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {announcement.title}
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        {IS_MANAGER && <>
                        <TableCell sx={{maxWidth: 400}} align="center">
                            <Tooltip title={announcement.seen_by}>
                                <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {announcement.seen_by}
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{maxWidth: 400}} align="center">
                            <Tooltip title={announcement.status}>
                                <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {announcement.status}
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell align="center" id="actions">
                            {announcement.status === AnnouncementStatus.DRAFT && <Tooltip title="Edit Draft Announcement">
                                <IconButton onClick={() => handleEditAnnouncement(announcement)}><Edit /></IconButton> 
                            </Tooltip>}
                            {announcement.status === AnnouncementStatus.DRAFT && <Tooltip title="Delete Draft Announcement">
                                <IconButton onClick={() => {setShowWarningDialog(true); setDeleteAnnouncementId(announcement.id)}}><Delete /></IconButton> 
                            </Tooltip>}
                            {(announcement.status === AnnouncementStatus.SENT || announcement.status === AnnouncementStatus.PARTIALLY_SENT) && <Tooltip title="Copy to New">
                                <IconButton onClick={() => handleCopyToNew(announcement)}><ContentCopy /></IconButton> 
                            </Tooltip>}
                            {announcement.status === AnnouncementStatus.PARTIALLY_SENT && <Tooltip title="Resend to failed users">
                                <IconButton onClick={() => handleSendAnnouncement(announcement.id)}><Loop /></IconButton>
                            </Tooltip>}
                            {announcement.status === AnnouncementStatus.FAILED_TO_SEND && <Tooltip title="Retry Sending">
                                <IconButton onClick={() => handleSendAnnouncement(announcement.id)}><Loop /></IconButton>
                            </Tooltip>}
                            {announcement.status === AnnouncementStatus.DRAFT && <Tooltip title="Send Announcement">
                                <IconButton onClick={() => handleSendAnnouncement(announcement.id)}><Send /></IconButton> 
                            </Tooltip>}
                        </TableCell></>}
                    </TableRow>
                )}
            </TableBody>
        </Table>)
    }

    if (!item.length) {
        return (
            <Paper elevation={3} sx={{borderRadius: 3, padding: 2, alignItems: 'center', display: 'flex', justifyContent: 'center'}}>
                <Typography variant="subtitle1" fontWeight={600} color="textSecondary">No Announcements Available</Typography>
            </Paper>
        )
    }

    const handleCopyToNew = (announcement: Announcements) => {
        navigate('/createAnnouncement', { state: { copy: true, announcement: announcement } });
    }

    return (
        <>
            <WarningDialog
                open={showWarningDialog}
                title="Delete Announcement"
                message="Are you sure you want to delete this announcement?"
                onConfirm={() => handleDeleteAnnouncement()}
                onClose={() => setShowWarningDialog(false)}
            />
            <Paper elevation={3} sx={{borderRadius: 3, overflow: 'auto'}}>
                {getTableData(item)}
            </Paper>
        </>
    )
}

export default BuildAnnouncementTable;